import classNames from 'classnames';

import { Button, Group, Stack, Text } from '@ekhodealer/ekho-common/rev';

import type { ConversationListRow } from '@ekhodealer/ekho-common';

import { formatChannelLabels } from '@common/lib/strings';
import { formatCompactRelativeTime } from '@common/lib/time';

import { ConversationStatusTag } from './ConversationStatusTag';
import { SenderAvatar } from './SenderAvatar';

import './ConversationListItem.scss';

interface ConversationListItemProps {
	/** The conversation data to render. */
	conversation: ConversationListRow;
	/** Whether this item is currently selected. */
	isSelected: boolean;
	/** Callback invoked with the conversation ID when the item is pressed. */
	onSelect: (conversationId: string) => void;
}

function isUnread(conversation: ConversationListRow): boolean {
	return conversation.status === 'active' && conversation.lastMessage?.role === 'lead';
}

export const ConversationListItem = ({
	conversation,
	isSelected,
	onSelect,
}: ConversationListItemProps): React.ReactNode => {
	const unread = isUnread(conversation);
	const timeLabel = formatCompactRelativeTime(
		conversation.lastMessage?.createdAt ?? conversation.updatedAt,
	);
	const preview = conversation.lastMessage?.content ?? '';
	const title = conversation.contactName || conversation.interest || 'Conversation';

	const handlePress = (): void => {
		onSelect(conversation.conversationId);
	};

	return (
		<Button
			variant={null}
			rounding='0'
			isFullWidth
			onPress={handlePress}
			className={classNames('conversation-list-item', {
				'conversation-list-item--selected': isSelected,
				'conversation-list-item--unread': unread && !isSelected,
			})}
			ariaLabel={
				conversation.contactName ? `Conversation with ${conversation.contactName}` : title
			}>
			{unread && !isSelected && <div className='conversation-list-item__unread-bar' />}
			<Stack margin='12' itemsSpacing='4'>
				<Group itemsAlignY='center' itemsAlignX='space-between'>
					<Group itemsSpacing='8' itemsAlignY='center' itemsAlignX='start'>
						{conversation.contactName && (
							<SenderAvatar senderType='lead' senderName={conversation.contactName} />
						)}
						<Text size='bodySmall' weight='semibold'>
							{title}
						</Text>
						<ConversationStatusTag status={conversation.status} />
					</Group>
					<Text size='footnote' color='secondary'>
						{timeLabel}
					</Text>
				</Group>
				<Group itemsSpacing='6' itemsAlignY='center' itemsAlignX='start'>
					<Text size='footnote' color='secondary'>
						{formatChannelLabels(conversation.channels)}
					</Text>
				</Group>
				{preview && (
					<Group noWrap>
						<Text
							size='bodySmall'
							color='secondary'
							align='left'
							className='conversation-list-item__preview'>
							{preview}
						</Text>
					</Group>
				)}
			</Stack>
		</Button>
	);
};
